/**
 * MarkerLayer renders markers (map annotations with NATO milsymbols)
 * on the Leaflet map. Markers are distinct from units and are always
 * draggable since they are web-created annotations.
 *
 * On drag end, the new position is persisted via the REST API (the
 * backend then publishes the MQTT update, so no direct MQTT publish
 * is needed here).
 *
 * Clicking a marker raises `onSelectMarker` to the parent (MapView),
 * which in turn mounts the `MarkerActionOverlay` over that marker.
 * We suppress the click that Leaflet fires immediately after a drag
 * ends so drags do not accidentally surface the action overlay.
 *
 * In Lines mode the click means something else entirely: it chooses the marker
 * as the parent of the next drawn line (an attack line from a spotted enemy,
 * say) instead of opening the action overlay. Same gesture, and the mode is
 * already visible in the toolbox, so nothing is hidden.
 */

import React, { useState, useCallback, useRef } from "react";
import { DivIcon, Marker as LeafletMarker } from "leaflet";
import { Marker } from "react-leaflet";
import ms from "milsymbol";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../store";
import { markersSlice } from "../../store/markersSlice";
import { useAppDispatch } from "../map/mapSlice";
import { MapEntity } from "./mapTypes.d";
import { setGraphicAttachment } from "./mapUiSlice";
import * as markerService from "../../services/markerService";

interface MarkerLayerProps {
    /**
     * Callback invoked when the user clicks a marker (post-drag-guard).
     * The parent typically records the marker ID to mount an overlay.
     */
    onSelectMarker: (marker: MapEntity) => void;
    /**
     * True while the Lines tool is active, in which case a marker click picks
     * that marker as the next line's parent rather than opening the overlay.
     */
    linesMode?: boolean;
}

export default function MarkerLayer({
    onSelectMarker,
    linesMode = false,
}: MarkerLayerProps) {
    const markers = useSelector((state: RootState) => state.markers.value);
    const { eventId } = useParams<{ eventId: string }>();
    const dispatch = useAppDispatch();
    const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);

    // Drag-guard: Leaflet fires `click` immediately after a drag ends.
    // We flip this flag true on `dragstart` and reset it a tick after
    // `dragend` so the synthetic click that follows is swallowed.
    const draggingRef = useRef<Set<string>>(new Set());

    const handleMoveEnd = useCallback(
        (marker: MapEntity, event: { target: LeafletMarker }) => {
            const ll = event.target.getLatLng();
            const updatedMarker: MapEntity = {
                ...marker,
                latLng: { lat: ll.lat, lng: ll.lng },
            };
            dispatch(markersSlice.actions.updateMarker(updatedMarker));

            if (eventId) {
                markerService.recordMarkerPosition(
                    eventId,
                    marker.id,
                    ll.lat,
                    ll.lng
                ).catch((err) => {
                    console.error("Failed to persist marker position:", err);
                });
            }
        },
        [dispatch, eventId]
    );

    return (
        <>
            {markers
                .filter((m) => m.latLng.lat !== 0 || m.latLng.lng !== 0)
                .map((marker) => (
                    <Marker
                        key={marker.id}
                        position={marker.latLng}
                        draggable={true}
                        icon={(() => {
                            const { dtg, ...renderProps } = marker.markerProperties || {} as Record<string, unknown>;
                            const isHovered = hoveredMarkerId === marker.id;
                            // `uniqueDesignation` is derived from the entity's
                            // name here, at the point of drawing — it is never
                            // stored in markerProperties. It comes last so a
                            // stale stored value can never win over `name`.
                            const sym = new ms.Symbol(marker.code, {
                                size: 32,
                                ...renderProps,
                                ...(isHovered && dtg ? { dtg: dtg as string } : {}),
                                ...(marker.name ? { uniqueDesignation: marker.name } : {}),
                            });
                            const anchor = sym.getAnchor();
                            return new DivIcon({
                                html: sym.asSVG(),
                                iconAnchor: [anchor.x, anchor.y],
                                iconSize: [sym.getSize().width, sym.getSize().height],
                            });
                        })()}
                        eventHandlers={{
                            mouseover: () => setHoveredMarkerId(marker.id),
                            mouseout: () => setHoveredMarkerId(null),
                            dragstart: () => {
                                draggingRef.current.add(marker.id);
                            },
                            dragend: () => {
                                // Defer clearing the flag so the synthetic
                                // click that follows dragend sees it as true.
                                setTimeout(() => {
                                    draggingRef.current.delete(marker.id);
                                }, 0);
                            },
                            click: () => {
                                if (draggingRef.current.has(marker.id)) {
                                    return; // Suppress post-drag click.
                                }
                                if (linesMode) {
                                    // Clicking the same marker again detaches
                                    // (the reducer toggles), so a standalone
                                    // line stays reachable without leaving the
                                    // tool. The marker's own SIDC rides along so
                                    // the line inherits its affiliation.
                                    dispatch(setGraphicAttachment({
                                        kind: 'marker',
                                        id: marker.id,
                                        sidc: marker.code,
                                    }));
                                    return;
                                }
                                onSelectMarker(marker);
                            },
                            moveend: (event) => {
                                handleMoveEnd(
                                    marker,
                                    event as { target: LeafletMarker }
                                );
                            },
                        }}
                    />
                ))}
        </>
    );
}
